import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { MultiSelect } from "@/components/multi-select"
import { useAuth } from "@/contexts/AuthContext"
import { listTeachersBySchool, listGrantees, updateShareGrantees } from "@/services/generatedAPIs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { CircleHelp } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface ShareAssignmentDialogProps {
  assignmentId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareAssignmentDialog({ assignmentId, open, onOpenChange }: ShareAssignmentDialogProps) {
  const { getCurrentUser } = useAuth();
  const { t } = useTranslation();
  const [teacherOptions, setTeacherOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (!open) {
      setDataLoaded(false);
      return;
    }

    const loadData = async () => {
      const currentUser = getCurrentUser()!;

      const [teachersRsp, granteesRsp] = await Promise.all([
        listTeachersBySchool({ schoolName: currentUser.schoolName }),
        listGrantees({ assignmentId }),
      ]);

      if (teachersRsp.code === 200 && teachersRsp.data?.teachers) {
        const options = teachersRsp.data.teachers
          .filter(t => t.id !== currentUser.id)
          .map(t => ({ label: t.name || t.email || `ID:${t.id}`, value: String(t.id) }));
        setTeacherOptions(options);
      }

      if (granteesRsp.code === 200 && granteesRsp.data) {
        setSelectedIds(granteesRsp.data.map(g => String(g.granteeId)));
      }

      setDataLoaded(true);
    };

    loadData().catch(console.error);
  }, [open, assignmentId, getCurrentUser]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const rsp = await updateShareGrantees({
        assignmentId,
        granteeIds: selectedIds.map(id => Number(id)),
      });
      if (rsp.code === 200) {
        toast.success(t('teacher.shareSuccess'));
        onOpenChange(false);
      }
    } catch (e) {
      console.error('updateShareGrantees error', e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-sm" onOpenAutoFocus={e => e.preventDefault()} onInteractOutside={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-1">
            {t('teacher.shareAssignment')}
            {' '}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CircleHelp className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">{t('teacher.shareWithTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {dataLoaded ? (
            <MultiSelect
              options={teacherOptions}
              onValueChange={setSelectedIds}
              defaultValue={selectedIds}
              placeholder={t('teacher.shareWith')}
              variant="default"
              modalPopover={true}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('component.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t('common.loading') : t('component.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
